import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { SearchInput } from '../../../components/searchInput/searchInput';
import { AgreementStatusFilterValue, getColumnSortOrder, sortByField } from '../../../components/table/columnFormatHelpers';
import { WithEmptyDataTable } from '../../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../../components/useDocumentTitle';
import { defaultDateFormat, defaultDateTimeFormat } from '../../../domain/common/dateFormatters';
import { exportSanSourceInfos } from '../../../domain/site';
import { ISanSourceInfo } from '../../../domain/site/interface';
import { useSanSourceInfoListQuery } from '../../../domain/site/queries';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { filterSanSourceInfos } from '../../../domain/agreement-status/helpers';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { CustomizableTable, CustomizableColumnType } from '../../../components/table/CustomizableTable';
import { useAgreementStatusesSidebar } from './sidebar/useAgreementStatusesSidebar';
import { AgreementStatusesSidebar } from './sidebar/AgreementStatusesSidebar';
import { PageFooter } from '../../../components/PageFooter';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';

export interface IAgreementStatusesPageState {
    filter?: {
        companyId?: number[] | number;
        agreementStatus?: AgreementStatusFilterValue;
        source?: string[] | string;
    };
    search?: string;
    pagination?: {
        pageSize: number;
        current: number;
    };
    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export const AgreementStatusesPage = () => {
    const { notification } = App.useApp();
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: IAgreementStatusesPageState = queryToState(location.search);
    const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

    useDocumentTitle('Agreement Statuses');

    const { sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useAgreementStatusesSidebar();

    const { data, isError, error, isFetching } = useSanSourceInfoListQuery({ keepPreviousData: true });

    const sanSourceInfos: ISanSourceInfo[] = useMemo(() => {
        const search = pageState?.search?.toLowerCase() || '';

        return filterSanSourceInfos(data, pageState.filter, search);
    }, [data, pageState?.search, pageState?.filter]);

    if (isError) {
        notification.error({ key: 'agreements-fetch-error', message: error?.message || 'Cannot fetch agreement statuses!' });
    }

    async function onTableChange(
        pagination: TablePaginationConfig,
        _: Record<string, FilterValue | null>, // not used table filter
        sorter: SorterResult<ISanSourceInfo> | SorterResult<ISanSourceInfo>[]
    ) {
        const preparedParams = toUsefulParams({ pagination, sorter });
        setPageQuery({ ...pageState, ...preparedParams });
    }

    async function onSearch(search: string) {
        setPageQuery({ ...pageState, search: search });
    }

    async function exportAgreementsStatuses() {
        try {
            setIsExportLoading(true);

            await exportSanSourceInfos();
        } catch (err) {
            notification.error({
                key: 'agreements-export-error',
                message: (err as Error)?.message || 'Cannot export agreement statuses!',
            });
        }

        setIsExportLoading(false);
    }

    const agreementStatusesColumns: CustomizableColumnType<ISanSourceInfo>[] = [
        {
            title: 'Company',
            dataIndex: 'companyName',
            key: 'companyName',
            render: (text: string, record: ISanSourceInfo) => record.companyName || '',

            sorter: (a, b) => sortByField(a, b, 'companyName'),
            ...getColumnSortOrder('companyName', pageState.sorter),
        },
        {
            title: 'Site',
            dataIndex: 'siteName',
            key: 'siteName',
            render: (text: string, record: ISanSourceInfo) => record.siteName,

            sorter: (a, b) => sortByField(a, b, 'siteName'),
            ...getColumnSortOrder('siteName', pageState.sorter),
        },
        {
            title: 'Enrollment ID',
            dataIndex: 'enrollmentId',
            key: 'enrollmentId',
            render: (text: string, record: ISanSourceInfo) => record.enrollmentId,

            sorter: (a, b) => sortByField(a, b, 'enrollmentId'),
            ...getColumnSortOrder('enrollmentId', pageState.sorter),
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            render: (text: string, record: ISanSourceInfo) => record.source,

            sorter: (a, b) => sortByField(a, b, 'source'),
            ...getColumnSortOrder('source', pageState.sorter),
        },
        {
            title: 'Retail Customer',
            dataIndex: 'retailCustomer',
            key: 'retailCustomer',
            render: (text: string, record: ISanSourceInfo) => record.retailCustomer,

            sorter: (a, b) => sortByField(a, b, 'retailCustomer'),
            ...getColumnSortOrder('retailCustomer', pageState.sorter),
        },
        {
            title: 'Usage Point',
            dataIndex: 'usagePoint',
            key: 'usagePoint',
            render: (text: string, record: ISanSourceInfo) => record.usagePoint,

            sorter: (a, b) => sortByField(a, b, 'usagePoint'),
            ...getColumnSortOrder('usagePoint', pageState.sorter),
        },
        {
            title: 'Last Interval Data Date',
            dataIndex: 'latestIntervalDataDate',
            key: 'latestIntervalDataDate',
            render: (text: string | null, record: ISanSourceInfo) =>
                record.latestIntervalDataDate ? defaultDateTimeFormat(new Date(record.latestIntervalDataDate)) : '-',

            sorter: (a, b) => sortByField(a, b, 'latestIntervalDataDate'),
            ...getColumnSortOrder('latestIntervalDataDate', pageState.sorter),
        },
        {
            title: 'Agreement Status',
            dataIndex: 'agreement.status',
            key: 'agreement.status',
            render: (status: string | null, record: ISanSourceInfo) => record.agreement?.status || '-',

            sorter: (a, b) => sortByField(a, b, 'agreement.status'),
            ...getColumnSortOrder('agreement.status', pageState.sorter),
        },
        {
            title: 'Agreement Start Date',
            dataIndex: 'agreement.startAt',
            key: 'agreement.startAt',
            render: (text: string | null, record: ISanSourceInfo) =>
                record.agreement?.startAt ? defaultDateFormat(new Date(record.agreement.startAt)) : '-',

            sorter: (a, b) => sortByField(a, b, 'agreement.startAt'),
            ...getColumnSortOrder('agreement.startAt', pageState.sorter),
        },
        {
            title: 'Agreement End Date',
            dataIndex: 'agreement.endAt',
            key: 'agreement.endAt',
            render: (text: string | null, record: ISanSourceInfo) =>
                record.agreement?.endAt ? defaultDateFormat(new Date(record.agreement.endAt)) : '-',

            sorter: (a, b) => sortByField(a, b, 'agreement.endAt'),
            ...getColumnSortOrder('agreement.endAt', pageState.sorter),
        },
    ];

    return (
        <>
            <CustomizableTableContextProvider configKey="agreementStatuses" columns={agreementStatusesColumns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    <AgreementStatusesSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                        totalRecords={sanSourceInfos?.length || 0}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                    />
                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle="Agreement Statuses"
                            actions={[
                                <SearchInput key="agreement-search" onSearch={onSearch} defaultValue={pageState?.search} />,
                                <Button
                                    size="large"
                                    key="download-csv"
                                    onClick={exportAgreementsStatuses}
                                    icon={<DownloadOutlined />}
                                    loading={isExportLoading}
                                    disabled={isFetching}
                                >
                                    Download CSV
                                </Button>,
                            ]}
                        />
                        <WithEmptyDataTable condition={isFetching}>
                            <CustomizableTable<ISanSourceInfo>
                                size="small"
                                rowKey="id"
                                sticky={{ offsetHeader: 62 }}
                                showHiddenColumnsCount={true}
                                dataSource={sanSourceInfos}
                                loading={isFetching}
                                onChange={onTableChange}
                                meta={{
                                    total: sanSourceInfos.length,
                                    limit: pageState?.pagination?.pageSize || 10,
                                    offset: pageState?.pagination?.current
                                        ? (pageState.pagination.current - 1) * (pageState?.pagination?.pageSize || 10)
                                        : 0,
                                }}
                            />
                        </WithEmptyDataTable>
                        <PageFooter />
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>
        </>
    );
};
